import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import PrintIcon from "@mui/icons-material/Print";
import RefreshIcon from "@mui/icons-material/Refresh";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  GlobalStyles,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import moment from "moment";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import Link from "@mui/material/Link";
import { useRecoilValue } from "recoil";
import {
  useGetItemsFluctuationData,
  useGetItemsFluctuationReport,
} from "src/api/reports";
import { useGetItemsList } from "src/api/purchasing/items";
import { useGetBranches } from "src/api/generic";
import DateRangeInput from "src/shared/components/date-range";
import {
  ReportControlBar,
  ReportDataTable,
  reportLayoutTokens,
  ReportPageHeading,
  ReportSectionCard,
  type ReportTableColumn,
} from "src/shared/components/report-layout";
import { authState } from "src/store/auth";
import RightArrow from "src/assets/svg-icons/aggregators/right_arrow.svg";
import {
  downloadAsXLS,
  oneBranchHasPermission,
  specificBranchHasPermission,
} from "src/utils";

type FluctuationRow = {
  itemId: number;
  itemName: string;
  sku: string;
  fluctuationPercent: number;
};

type FluctuationItemApi = {
  item_id?: number;
  item_name: string;
  sku: string | number | null;
  fluctuation_percent: number;
};

function unwrapFluctuationItems(res: unknown): FluctuationItemApi[] {
  if (!res || typeof res !== "object") return [];
  const o = res as Record<string, unknown>;
  const inner = o.data;
  if (inner && typeof inner === "object") {
    const items = (inner as Record<string, unknown>).items;
    if (Array.isArray(items)) return items as FluctuationItemApi[];
  }
  if (Array.isArray(o.items)) return o.items as FluctuationItemApi[];
  return [];
}

type TopItemsFluctuationFilters = {
  startDate: string;
  endDate: string;
  branchId: string;
};

const parseTopItemsFilters = (sp: URLSearchParams): TopItemsFluctuationFilters => ({
  startDate:
    sp.get("start_date") ||
    moment().startOf("month").format("yyyy-MM-DD"),
  endDate:
    sp.get("end_date") ||
    moment().endOf("month").format("yyyy-MM-DD"),
  branchId: sp.get("branch_id") || "",
});

/** Fluctuation as plain numbers, negatives in accounting parentheses, single text color. */
const formatFluctuationAccounting = (p: number) => {
  const n = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  if (p < 0) return `(${n.format(Math.abs(p))})`;
  return n.format(p);
};

const TopItemsFluctuationsPage = () => {
  const { t } = useTranslation("reports");
  const { t: purchasingT } = useTranslation("purchasing");
  const { t: generalT } = useTranslation("general");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const auth = useRecoilValue(authState);
  const companyName = auth.user?.company?.name;

  const [appliedFilters, setAppliedFilters] = useState<TopItemsFluctuationFilters>(() =>
    parseTopItemsFilters(new URLSearchParams(searchParams.toString()))
  );
  const [draftFilters, setDraftFilters] = useState<TopItemsFluctuationFilters>(() =>
    parseTopItemsFilters(new URLSearchParams(searchParams.toString()))
  );

  const [draftItemId, setDraftItemId] = useState<string>("");
  const [draftSku, setDraftSku] = useState<string>("");
  const [appliedItemId, setAppliedItemId] = useState<string>("");
  const [appliedSku, setAppliedSku] = useState<string>("");
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [modalItemIdDraft, setModalItemIdDraft] = useState<string>("");
  const [modalSkuDraft, setModalSkuDraft] = useState<string>("");

  const { startDate, endDate, branchId: branchIdParam } = appliedFilters;

  const { data: branchesData } = useGetBranches();

  const branchesHavePermission =
    branchesData?.data.filter((branch) =>
      specificBranchHasPermission("download_third_eye_report", branch.id)
    ) || [];

  const branchIdNum = branchIdParam ? Number(branchIdParam) : NaN;
  const branchValid = Number.isFinite(branchIdNum);

  const draftBranchIdNum = draftFilters.branchId ? Number(draftFilters.branchId) : NaN;

  const { data: itemsCatalogPayload } = useGetItemsList({
    branch_id: Number.isFinite(draftBranchIdNum) ? draftBranchIdNum : undefined,
    enabled: filterModalOpen && Number.isFinite(draftBranchIdNum),
  });

  const {
    data: reportResponse,
    isFetching,
    refetch: refetchReport,
  } = useGetItemsFluctuationData({
    start_date: startDate,
    end_date: endDate,
    branches: branchValid ? [branchIdNum] : [],
    enabled: branchValid,
  });

  const { refetch: refetchFluctuationReport, isFetching: isDownloadingReport } =
    useGetItemsFluctuationReport({
      start_date: startDate,
      end_date: endDate,
      branches: branchValid ? [branchIdNum] : [],
      enabled: false,
    });

  const rows: FluctuationRow[] = useMemo(() => {
    const items = unwrapFluctuationItems(reportResponse);
    if (!items?.length) return [];
    return items.map((item) => ({
      itemId: item.item_id ?? 0,
      itemName: item.item_name,
      sku: String(item.sku ?? ""),
      fluctuationPercent: item.fluctuation_percent,
    }));
  }, [reportResponse]);

  const itemOptions = useMemo(() => {
    const byId = new Map<number, string>();
    (itemsCatalogPayload?.data ?? []).forEach((it: { id: number; name?: string }) => {
      byId.set(Number(it.id), String(it.name ?? ""));
    });
    rows.forEach((r) => {
      byId.set(r.itemId, r.itemName);
    });
    return Array.from(byId.entries()).sort((a, b) =>
      a[1].localeCompare(b[1], undefined, { sensitivity: "base" })
    );
  }, [itemsCatalogPayload?.data, rows]);

  const skuOptions = useMemo(() => {
    const skus = new Set<string>();
    (itemsCatalogPayload?.data ?? []).forEach((it: { sku?: string | number }) => {
      const s = String(it?.sku ?? "").trim();
      if (s) skus.add(s);
    });
    rows.forEach((r) => {
      const s = r.sku?.trim();
      if (s) skus.add(s);
    });
    return Array.from(skus).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [itemsCatalogPayload?.data, rows]);

  const filteredRows = useMemo(() => {
    const idStr = appliedItemId.trim();
    const skuStr = appliedSku.trim();
    if (!idStr && !skuStr) return rows;
    const idNum = idStr ? Number(idStr) : NaN;
    const idOk = idStr && Number.isFinite(idNum);
    return rows.filter((r) => {
      if (idOk && r.itemId !== idNum) return false;
      if (skuStr && r.sku.trim() !== skuStr) return false;
      return true;
    });
  }, [rows, appliedItemId, appliedSku]);

  const syncAppliedToUrl = useCallback(
    (next: TopItemsFluctuationFilters) => {
      const p = new URLSearchParams();
      p.set("start_date", next.startDate);
      p.set("end_date", next.endDate);
      if (next.branchId) p.set("branch_id", next.branchId);
      setSearchParams(p);
    },
    [setSearchParams]
  );

  const openFilterModal = () => {
    setModalItemIdDraft(draftItemId);
    setModalSkuDraft(draftSku);
    setFilterModalOpen(true);
  };

  const cancelFilterModal = () => setFilterModalOpen(false);

  const commitFiltersFromModal = () => {
    setDraftItemId(modalItemIdDraft);
    setDraftSku(modalSkuDraft);
    setFilterModalOpen(false);
  };

  const handleReportUpdate = () => {
    setAppliedFilters({ ...draftFilters });
    setAppliedItemId(draftItemId.trim());
    setAppliedSku(draftSku.trim());
    syncAppliedToUrl(draftFilters);
    const bid = draftFilters.branchId ? Number(draftFilters.branchId) : NaN;
    if (Number.isFinite(bid)) void refetchReport();
  };

  const downloadReportsPermission = oneBranchHasPermission(
    "download_third_eye_report"
  );

  const periodLabel = useMemo(() => {
    return t("reportDateRange", {
      start: moment(startDate).format("D MMM YYYY"),
      end: moment(endDate).format("D MMM YYYY"),
    });
  }, [t, startDate, endDate]);

  const handleDownload = () => {
    if (!branchValid) return;
    void refetchFluctuationReport().then((res) => {
      if (res.data != null) {
        downloadAsXLS(`\uFEFF${res.data}`, "items-fluctuation");
      }
    });
  };

  const handlePrint = () => {
    window.print();
  };

  const columns: ReportTableColumn<FluctuationRow>[] = useMemo(
    () => [
      {
        id: "item",
        header: t("itemName"),
        cell: (row) => {
          const to = row.itemId
            ? `/reports/item-detail?${new URLSearchParams({
                start_date: startDate,
                end_date: endDate,
                item_id: String(row.itemId),
                ...(branchIdParam ? { branch_id: branchIdParam } : {}),
              }).toString()}`
            : undefined;
          return to ? (
            <Tooltip title={t("viewItemDetail")}>
              <Link
                component={RouterLink}
                to={to}
                underline="always"
                sx={{ fontSize: "inherit" }}
              >
                <Box component="span" dir="auto">
                  {row.itemName}
                </Box>
              </Link>
            </Tooltip>
          ) : (
            <Box component="span" dir="auto">
              {row.itemName}
            </Box>
          );
        },
      },
      {
        id: "sku",
        header: t("sku"),
        align: "center",
        width: 140,
        cell: (row) => (
          <Typography
            color="text.secondary"
            textAlign="center"
            display="block"
            fontSize="0.875rem"
          >
            {row.sku}
          </Typography>
        ),
      },
      {
        id: "fluct",
        header: t("fluctuationPercent"),
        align: "right",
        cell: (row) => (
          <Box
            component="span"
            fontWeight={700}
            color="#101828"
            display="block"
            textAlign="right"
          >
            {formatFluctuationAccounting(row.fluctuationPercent)}
          </Box>
        ),
      },
    ],
    [t, startDate, endDate, branchIdParam]
  );

  return (
    <Box p={{ xs: 2, sm: "12px 32px 32px" }} maxWidth="100%">
      <GlobalStyles
        styles={{
          "@media print": {
            "body *": { visibility: "hidden" },
            "#top-items-print-root, #top-items-print-root *": {
              visibility: "visible",
            },
            "#top-items-print-root": {
              position: "absolute",
              left: 0,
              top: 0,
              width: "100%",
            },
          },
        }}
      />

      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        color="#101828"
        mb={1.5}
        sx={{ "@media print": { display: "none" } }}
      >
        <Typography
          fontSize="0.9rem"
          color="#475467"
          onClick={() => navigate("/reports")}
          sx={{ cursor: "pointer" }}
        >
          {t("thirdEyeReport")}
        </Typography>
        <img src={RightArrow} alt="" style={{ width: 12, height: 12 }} />
        <Box borderRadius="4px" bgcolor="#EAECF0" px={1} py={0.5}>
          <Typography color="#344054" fontWeight={600} fontSize="0.85rem">
            {purchasingT("topItemsFluctuations")}
          </Typography>
        </Box>
      </Stack>

      <Box sx={{ "@media print": { display: "none" } }} mb={2}>
        <ReportControlBar
          left={
            <>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="top-items-branch" shrink>
                  {generalT("branch")}
                </InputLabel>
                <Select
                  labelId="top-items-branch"
                  label={generalT("branch")}
                  value={draftFilters.branchId}
                  onChange={(e) => {
                    const v = e.target.value as string;
                    setDraftFilters((p) => ({ ...p, branchId: v }));
                  }}
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>{generalT("selectBranch")}</em>
                  </MenuItem>
                  {branchesHavePermission.map((branch) => (
                    <MenuItem key={branch.id} value={String(branch.id)}>
                      {branch.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ minWidth: 250 }}>
                <DateRangeInput
                  startDate={
                    draftFilters.startDate ? moment(draftFilters.startDate) : null
                  }
                  startDateId="top-items-fluct-start"
                  endDate={
                    draftFilters.endDate
                      ? moment(draftFilters.endDate)
                      : null
                  }
                  endDateId="top-items-fluct-end"
                  onDatesChange={(arg: {
                    startDate: moment.Moment | null;
                    endDate: moment.Moment | null;
                  }) => {
                    const s = arg.startDate?.format("yyyy-MM-DD");
                    const e = arg.endDate?.format("yyyy-MM-DD");
                    if (s && e) {
                      setDraftFilters((p) => ({
                        ...p,
                        startDate: s,
                        endDate: e,
                      }));
                    }
                  }}
                />
              </Box>
            </>
          }
          center={
            <>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<FilterListIcon fontSize="small" />}
                onClick={openFilterModal}
                sx={{
                  height: 40,
                  minWidth: 100,
                  borderColor: "#D0D5DD",
                  color: "#344054",
                }}
              >
                {t("filter")}
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<RefreshIcon fontSize="small" />}
                onClick={handleReportUpdate}
                disabled={!Number.isFinite(draftBranchIdNum) || isFetching}
                sx={{ height: 40, minWidth: 120 }}
              >
                {t("reportUpdate")}
              </Button>
            </>
          }
          right={
            <>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<PrintIcon fontSize="small" />}
                onClick={handlePrint}
                sx={{
                  height: 40,
                  minWidth: 100,
                  borderColor: "#D0D5DD",
                  color: "#344054",
                }}
              >
                {t("print")}
              </Button>
              <LoadingButton
                variant="outlined"
                startIcon={<FileDownloadIcon fontSize="small" />}
                onClick={handleDownload}
                loading={isDownloadingReport}
                disabled={!branchValid || !downloadReportsPermission}
                sx={{
                  height: 40,
                  minWidth: 100,
                  borderColor: "#D0D5DD",
                  color: "#344054",
                }}
              >
                {t("export")}
              </LoadingButton>
            </>
          }
        />
      </Box>

      <Dialog open={filterModalOpen} onClose={cancelFilterModal} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pr: 1 }}>
          <FilterListIcon sx={{ color: "#475467", fontSize: 22 }} />
          <Typography component="span" fontWeight={700} flex={1}>
            {t("filter")}
          </Typography>
          <IconButton aria-label="close" onClick={cancelFilterModal} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="top-items-modal-item" shrink>
                {t("filterModalItemName")}
              </InputLabel>
              <Select<string>
                labelId="top-items-modal-item"
                label={t("filterModalItemName")}
                value={modalItemIdDraft}
                displayEmpty
                onChange={(e) =>
                  setModalItemIdDraft(
                    typeof e.target.value === "string" ? e.target.value : ""
                  )
                }
                input={
                  <OutlinedInput notched label={t("filterModalItemName")} />
                }
                renderValue={(selected) => {
                  if (!selected) return t("filterModalItemName");
                  const id = Number(selected);
                  const name = itemOptions.find(([itemId]) => itemId === id)?.[1];
                  return name ?? selected;
                }}
                sx={{
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
                }}
              >
                <MenuItem value="">
                  <em>{t("filterModalItemName")}</em>
                </MenuItem>
                {itemOptions.map(([id, name]) => (
                  <MenuItem key={id} value={String(id)}>
                    <Box component="span" dir="auto">
                      {name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel id="top-items-modal-sku" shrink>
                {t("filterModalSku")}
              </InputLabel>
              <Select<string>
                labelId="top-items-modal-sku"
                label={t("filterModalSku")}
                value={modalSkuDraft}
                displayEmpty
                onChange={(e) =>
                  setModalSkuDraft(
                    typeof e.target.value === "string" ? e.target.value : ""
                  )
                }
                input={<OutlinedInput notched label={t("filterModalSku")} />}
                renderValue={(selected) =>
                  selected ? selected : t("filterModalSku")
                }
                sx={{
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
                }}
              >
                <MenuItem value="">
                  <em>{t("filterModalSku")}</em>
                </MenuItem>
                {skuOptions.map((sku) => (
                  <MenuItem key={sku} value={sku}>
                    <Typography component="span" dir="auto" variant="body2">
                      {sku}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, flexDirection: "column", gap: 1 }}>
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={commitFiltersFromModal}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {t("applyFilters")}
          </Button>
          <Button color="inherit" fullWidth onClick={cancelFilterModal} sx={{ textTransform: "none" }}>
            {generalT("cancel")}
          </Button>
        </DialogActions>
      </Dialog>

      <Box id="top-items-print-root">
        <ReportPageHeading
          title={purchasingT("topItemsFluctuations")}
          subtitle={companyName}
          periodLabel={periodLabel}
        />

        <ReportSectionCard>
          {!branchValid ? (
            <Box p={4} textAlign="center">
              <Typography color="text.secondary">
                {t("selectBranchToViewReport")}
              </Typography>
            </Box>
          ) : (
            <ReportDataTable<FluctuationRow>
              columns={columns}
              rows={filteredRows}
              getRowId={(row, i) => `${row.itemId}-${row.sku}-${i}`}
              isLoading={isFetching}
              tableHeaderBackground={reportLayoutTokens.tableHeaderBgAlt}
              tableHeaderTextColor={reportLayoutTokens.tableHeaderTextAlt}
              emptyMessage={
                <Typography color="text.secondary" align="center" py={2}>
                  {generalT("noData")}
                </Typography>
              }
            />
          )}
        </ReportSectionCard>
      </Box>
    </Box>
  );
};

export default TopItemsFluctuationsPage;
